import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import NotificationsToggle from "@/components/NotificationsToggle";
import TasksWidget from "@/components/TasksWidget";
import FailedWeekCard from "@/components/FailedWeekCard";
import ShoppingListCard from "@/components/ShoppingListCard";
import ChecklistCard from "@/components/ChecklistCard";
import GoogleUpcomingCard from "@/components/GoogleUpcomingCard";
import AssistantUsageCard from "@/components/AssistantUsageCard";
import AutomationsDigestCard from "@/components/AutomationsDigestCard";
import {
  Users,
  Scissors,
  Package,
  CalendarDays,
  TrendingUp,
  TrendingDown,
  Receipt,
  MessageCircle,
  Target,
  UserX,
  Sparkles,
  ClipboardList,
  Cake,
  Settings as SettingsIcon,
  Phone,
  Crown,
  ArrowUp,
  ArrowDown,
  Minus,
  CheckCircle2,
  Clock as ClockIcon,
  Clock,
  XCircle,
  Bell,
} from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { apiCall, user } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";
  const [stats, setStats] = useState(null);
  const [extended, setExtended] = useState(null);
  const [todayExp, setTodayExp] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ monthly_revenue_target: "0", business_days_per_month: "26" });

  const load = async () => {
    try {
      const [s, e, t, r] = await Promise.all([
        apiCall("get", "/dashboard/stats"),
        apiCall("get", "/dashboard/extended"),
        apiCall("get", "/dashboard/today-expectation"),
        apiCall("get", "/reminders/upcoming"),
      ]);
      setStats(s.data);
      setExtended(e.data);
      setTodayExp(t.data);
      setReminders(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReminderDone = async (id) => {
    try {
      await apiCall("put", `/notes/${id}`, { is_done: true });
      load();
    } catch {
      toast.error("Erro");
    }
  };

  useEffect(() => {
    load();
    // Load settings for budget editing
    apiCall("get", "/settings")
      .then((res) =>
        setSettingsForm({
          monthly_revenue_target: (res.data.monthly_revenue_target || 0).toString(),
          business_days_per_month: (res.data.business_days_per_month || 26).toString(),
        })
      )
      .catch(() => {});
  }, []);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await apiCall("put", "/settings", {
        monthly_revenue_target: parseFloat(settingsForm.monthly_revenue_target) || 0,
        business_days_per_month: parseInt(settingsForm.business_days_per_month) || 26,
      });
      toast.success("Meta atualizada!");
      setSettingsOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const sendWhatsApp = async (client, msg) => {
    try {
      const res = await apiCall("post", "/whatsapp/link", {
        phone: client.phone,
        message: msg,
      });
      window.open(res.data.link, "_blank");
    } catch {
      toast.error("Erro ao gerar link");
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const formatRelativeDate = (iso) => {
    if (!iso) return "Nunca";
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "Hoje";
    if (days === 1) return "Ontem";
    if (days < 30) return `${days} dias atrás`;
    if (days < 60) return "1 mês atrás";
    return `${Math.floor(days / 30)} meses atrás`;
  };

  const cards = [
    { label: "Clientes", value: stats?.total_clients ?? 0, icon: Users, testId: "stat-clients", to: "/clientes", tint: "liquid-card-tint-pink" },
    { label: "Serviços", value: stats?.total_services ?? 0, icon: Scissors, testId: "stat-services", to: "/servicos", tint: "liquid-card-tint-gold" },
    { label: "Produtos", value: stats?.total_products ?? 0, icon: Package, testId: "stat-products", to: "/produtos", tint: "liquid-card-tint-green" },
    { label: "Hoje", value: stats?.today_appointments ?? 0, icon: CalendarDays, testId: "stat-appointments-today", to: "/agenda", tint: "liquid-card-tint-blue" },
  ];

  const budget = extended?.budget;

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2 font-heading">
            Visão Geral
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light font-heading">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-2 font-body">
            Acompanhe os principais indicadores do seu salão
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationsToggle apiCall={apiCall} />
          {isMaster && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="settings-budget-button">
              <SettingsIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Definir Meta
            </Button>
          )}
        </div>
      </div>

      {/* Resumo de Automações (substitui WeeklySummary + SmartLeads + Atalhos) — leva pra /automacoes */}
      <AutomationsDigestCard apiCall={apiCall} navigate={navigate} />

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {cards.map((card) => (
          <Card
            key={card.label}
            className={`liquid-stat-card ${card.tint} p-4 lg:p-6 rounded-2xl cursor-pointer`}
            data-testid={card.testId}
            onClick={() => navigate(card.to)}
            role="button"
          >
            <div className="flex items-start justify-between relative z-10">
              <div>
                <div className="text-[10px] lg:text-xs uppercase tracking-widest text-foreground/60 mb-2 font-heading font-medium">
                  {card.label}
                </div>
                <div className="text-2xl lg:text-4xl font-light tracking-tight font-heading text-foreground">
                  {loading ? "—" : card.value}
                </div>
              </div>
              <div className="rounded-full bg-white/40 p-2 backdrop-blur-sm">
                <card.icon className="h-5 w-5 text-foreground/80" strokeWidth={1.5} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Budget tracking */}
      {budget && budget.monthly_target > 0 && (
        <Card className="border border-border bg-card p-5 lg:p-6 shadow-soft rounded-md" data-testid="budget-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-heading flex items-center gap-2">
                <Target className="h-3 w-3" strokeWidth={1.5} />
                Meta do Mês
              </div>
              <div className="text-2xl font-light tracking-tight font-heading">
                {formatCurrency(budget.monthly_realized)}{" "}
                <span className="text-sm text-muted-foreground">/ {formatCurrency(budget.monthly_target)}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-heading">Progresso</div>
              <div className="text-2xl font-light tracking-tight font-heading" style={{ color: budget.progress_pct >= 100 ? "#166534" : "#CBA871" }}>
                {budget.progress_pct.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative h-3 bg-secondary rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full transition-all duration-500"
              style={{
                width: `${Math.min(budget.progress_pct, 100)}%`,
                background: budget.progress_pct >= 100 ? "#166534" : "linear-gradient(90deg, #CBA871 0%, #B8956A 100%)",
              }}
              data-testid="budget-progress-bar"
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5 pt-4 border-t border-border">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Meta diária base</div>
              <div className="text-base font-medium" data-testid="budget-daily-target">{formatCurrency(budget.daily_target)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Sugerido p/ atingir</div>
              <div className="text-base font-medium" style={{ color: "#CBA871" }} data-testid="budget-daily-suggested">{formatCurrency(budget.daily_suggested)}/dia</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Faltam</div>
              <div className="text-base font-medium" data-testid="budget-remaining">{formatCurrency(budget.remaining_to_target)} em {budget.days_remaining} dias</div>
            </div>
          </div>
        </Card>
      )}

      {budget && budget.monthly_target === 0 && isMaster && (
        <Card className="border border-accent/40 bg-accent/5 p-4 lg:p-5 shadow-soft rounded-md">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 text-sm">
              <Target className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} style={{ color: "#CBA871" }} />
              <span className="text-muted-foreground">
                Defina uma meta de faturamento mensal para acompanhar o progresso diário sugerido.
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} data-testid="set-budget-cta">
              Definir Meta
            </Button>
          </div>
        </Card>
      )}

      {/* Today expectation vs realized */}
      {todayExp && todayExp.counts.total > 0 && (
        <Card className="border border-border bg-card p-5 lg:p-6 shadow-soft rounded-md" data-testid="today-expectation-card">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-heading flex items-center gap-2">
                <CalendarDays className="h-3 w-3" strokeWidth={1.5} />
                Expectativa do Dia
              </div>
              <h3 className="text-xl font-light tracking-tight font-heading">
                {todayExp.counts.concluded} de {todayExp.counts.expected} agendamentos finalizados
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {todayExp.counts.cancelled > 0 && (
                <span className="text-xs px-2 py-1 rounded font-medium" style={{ background: "#F3F4F6", color: "#737373" }}>
                  {todayExp.counts.cancelled} cancelado{todayExp.counts.cancelled > 1 ? "s" : ""}
                </span>
              )}
              {todayExp.counts.no_show > 0 && (
                <span className="text-xs px-2 py-1 rounded font-medium" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                  {todayExp.counts.no_show} faltou
                </span>
              )}
              {todayExp.counts.pending > 0 && (
                <span className="text-xs px-2 py-1 rounded font-medium" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                  {todayExp.counts.pending} pendente{todayExp.counts.pending > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-5">
            <div className="p-3 lg:p-4 bg-secondary/40 rounded-md" data-testid="today-expected">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Previsto</div>
              <div className="text-lg lg:text-xl font-medium font-heading">{formatCurrency(todayExp.expected_total)}</div>
            </div>
            <div className="p-3 lg:p-4 bg-secondary/40 rounded-md" data-testid="today-realized">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Realizado</div>
              <div className="text-lg lg:text-xl font-medium font-heading" style={{ color: "#166534" }}>
                {formatCurrency(todayExp.realized_total)}
              </div>
            </div>
            <div
              className="p-3 lg:p-4 rounded-md"
              style={{
                background: todayExp.difference > 0 ? "#D1FAE5" : todayExp.difference < 0 ? "#FEE2E2" : "#F3F4F6",
              }}
              data-testid="today-difference"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading flex items-center gap-1">
                {todayExp.difference > 0 ? <ArrowUp className="h-3 w-3" strokeWidth={2} /> : todayExp.difference < 0 ? <ArrowDown className="h-3 w-3" strokeWidth={2} /> : <Minus className="h-3 w-3" strokeWidth={2} />}
                Diferença
              </div>
              <div
                className="text-lg lg:text-xl font-medium font-heading"
                style={{ color: todayExp.difference > 0 ? "#166534" : todayExp.difference < 0 ? "#991B1B" : "#737373" }}
              >
                {todayExp.difference > 0 ? "+" : ""}
                {formatCurrency(todayExp.difference)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {todayExp.difference_pct > 0 ? "+" : ""}
                {todayExp.difference_pct}%
              </div>
            </div>
            <div className="p-3 lg:p-4 rounded-md" style={{ background: "#FEF3C7" }} data-testid="today-tips">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 font-heading">Gorjetas</div>
              <div className="text-lg lg:text-xl font-medium font-heading" style={{ color: "#CBA871" }}>
                {formatCurrency(todayExp.tips_total)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Total c/ gorjeta: {formatCurrency(todayExp.total_with_tips)}
              </div>
            </div>
          </div>

          {/* Insight message */}
          {todayExp.counts.concluded > 0 && (
            <div
              className="text-xs lg:text-sm p-3 rounded-md font-medium"
              style={{
                background: todayExp.difference > 0 ? "#D1FAE5" : todayExp.difference < 0 ? "#FEE2E2" : "#F3F4F6",
                color: todayExp.difference > 0 ? "#166534" : todayExp.difference < 0 ? "#991B1B" : "#374151",
              }}
              data-testid="today-insight"
            >
              {todayExp.difference > 0 && (
                <>🎉 Acima da expectativa! {formatCurrency(todayExp.difference)} a mais — possíveis upgrades ou venda extra.</>
              )}
              {todayExp.difference < 0 && (
                <>⚠️ Abaixo da expectativa em {formatCurrency(Math.abs(todayExp.difference))} — verifique cancelamentos ou descontos aplicados.</>
              )}
              {todayExp.difference === 0 && <>✓ Fechamento exatamente conforme planejado.</>}
            </div>
          )}

          {/* Per-appointment breakdown */}
          {todayExp.breakdown && todayExp.breakdown.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 font-heading">
                Detalhamento por Agendamento
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {todayExp.breakdown.map((b) => (
                  <BreakdownRow key={b.appointment_id} item={b} formatCurrency={formatCurrency} />
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
        <Card className="liquid-stat-card liquid-card-tint-green p-5 rounded-2xl" data-testid="financial-revenue">
          <div className="flex items-start justify-between mb-3 relative z-10">
            <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Receita Mês</div>
            <div className="rounded-full bg-white/40 p-1.5"><TrendingUp className="h-4 w-4" strokeWidth={1.5} style={{ color: "#166534" }} /></div>
          </div>
          <div className="text-xl lg:text-3xl font-light tracking-tight font-heading relative z-10" style={{ color: "#166534" }}>
            {loading ? "—" : formatCurrency(stats?.monthly_revenue)}
          </div>
        </Card>
        <Card className="liquid-stat-card liquid-card-tint-pink p-5 rounded-2xl" data-testid="financial-expenses">
          <div className="flex items-start justify-between mb-3 relative z-10">
            <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Despesas Mês</div>
            <div className="rounded-full bg-white/40 p-1.5"><TrendingDown className="h-4 w-4 text-destructive" strokeWidth={1.5} /></div>
          </div>
          <div className="text-xl lg:text-3xl font-light tracking-tight font-heading text-destructive relative z-10">
            {loading ? "—" : formatCurrency(stats?.monthly_expenses)}
          </div>
        </Card>
        <Card className="liquid-stat-card liquid-card-tint-gold p-5 rounded-2xl" data-testid="financial-profit">
          <div className="flex items-start justify-between mb-3 relative z-10">
            <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Lucro</div>
            <div className="rounded-full bg-white/40 p-1.5"><TrendingUp className="h-4 w-4" strokeWidth={1.5} style={{ color: "#CBA871" }} /></div>
          </div>
          <div className="text-xl lg:text-3xl font-light tracking-tight font-heading relative z-10">
            {loading ? "—" : formatCurrency(stats?.monthly_profit)}
          </div>
        </Card>
      </div>

      {/* Tasks widgets — dia + minhas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TasksWidget apiCall={apiCall} scope="mine" title="Para você (=mencionado)" testId="tasks-widget-mine" />
        <TasksWidget apiCall={apiCall} scope="all" title="Tarefas do dia (equipe)" testId="tasks-widget-all" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GoogleUpcomingCard />
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate("/gregory-ai")}
          className="cursor-pointer hover:shadow-md transition-shadow rounded-md"
          data-testid="assistant-usage-link"
        >
          <AssistantUsageCard />
        </div>
      </div>

      {/* Falhas da Semana — rows already navigate to /pendentes */}
      <FailedWeekCard apiCall={apiCall} />

      {/* Checklist Diário → Tarefas */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate("/tarefas")}
        className="cursor-pointer hover:shadow-md transition-shadow rounded-md"
        data-testid="checklist-link"
      >
        <ChecklistCard apiCall={apiCall} />
      </div>

      {/* Lista de Compras → Estoque */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate("/estoque")}
        className="cursor-pointer hover:shadow-md transition-shadow rounded-md"
        data-testid="shopping-list-link"
      >
        <ShoppingListCard apiCall={apiCall} />
      </div>

      {/* Upcoming reminders */}
      {reminders.length > 0 && (
        <Card className="border border-border bg-card p-5 lg:p-6 shadow-soft rounded-md" data-testid="reminders-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ background: "#FEF3C7" }}>
                <Bell className="h-5 w-5" strokeWidth={1.5} style={{ color: "#92400E" }} />
              </div>
              <div>
                <div className="text-sm font-medium">Lembretes Próximos</div>
                <div className="text-xs text-muted-foreground">Pendentes nos próximos 7 dias + atrasados</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">{reminders.length} {reminders.length === 1 ? "lembrete" : "lembretes"}</div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {reminders.map((r) => {
              const reminderDate = new Date(r.reminder_date);
              const isOverdue = reminderDate < new Date();
              const dateStr = reminderDate.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
              return (
                <div
                  key={r.note_id}
                  className="flex items-start gap-3 p-3 rounded-md hover:bg-secondary/50 transition-colors"
                  style={{ borderLeft: `3px solid ${isOverdue ? "#DC2626" : "#F59E0B"}` }}
                  data-testid={`reminder-${r.note_id}`}
                >
                  <Checkbox
                    checked={r.is_done}
                    onCheckedChange={() => handleToggleReminderDone(r.note_id)}
                    className="mt-1"
                    data-testid={`reminder-done-${r.note_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-sm">{r.client_name}</span>
                      {isOverdue && (
                        <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }}>
                          Atrasado
                        </span>
                      )}
                    </div>
                    {r.title && <div className="text-sm font-medium">{r.title}</div>}
                    <p className="text-sm text-muted-foreground line-clamp-2">{r.content}</p>
                    <div className="text-xs mt-1" style={{ color: isOverdue ? "#991B1B" : "#92400E" }}>
                      <Clock className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
                      {dateStr}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Tagged client groups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mecha Test */}
        <ClientGroupCard
          icon={Sparkles}
          color="#7C3AED"
          bg="#EDE9FE"
          title="Teste de Mechas"
          subtitle="Clientes marcados com tag de teste"
          clients={extended?.mecha_test_clients || []}
          emptyMsg='Marque clientes com a tag "mecha-teste" em Clientes'
          sendWhatsApp={sendWhatsApp}
          msgTemplate={(c) => `Oi ${c.name}! Tudo bem? Como você ficou após o teste de mechas? Já podemos agendar o procedimento completo? 💫`}
          testId="group-mecha-test"
          filterTo="/clientes?tag=mecha-teste"
        />

        {/* Pending Close */}
        <ClientGroupCard
          icon={ClipboardList}
          color="#F59E0B"
          bg="#FEF3C7"
          title="Pendentes de Fechamento"
          subtitle="Clientes para retomar a venda do agendamento"
          clients={extended?.pending_close_clients || []}
          emptyMsg='Marque clientes com tag "fechamento-pendente" em Clientes'
          sendWhatsApp={sendWhatsApp}
          msgTemplate={(c) => `Olá ${c.name}! Estou separando os materiais para o seu procedimento. Vamos confirmar o melhor dia para o seu atendimento? ✨`}
          testId="group-pending-close"
          filterTo="/clientes?tag=fechamento-pendente"
        />

        {/* Quotes / Orçamentos */}
        <ClientGroupCard
          icon={ClipboardList}
          color="#0EA5E9"
          bg="#E0F2FE"
          title="Orçamentos em Aberto"
          subtitle="Clientes com orçamento enviado aguardando retorno"
          clients={extended?.quotes_clients || []}
          emptyMsg='Marque clientes com a tag "orcamento" em Clientes'
          sendWhatsApp={sendWhatsApp}
          msgTemplate={(c) => `Oi ${c.name}! Tudo bem? Passando para saber o que achou do orçamento que enviei. Posso te ajudar com alguma dúvida? 💫`}
          testId="group-quotes"
          filterTo="/clientes?tag=orcamento"
        />

        {/* Birthdays */}
        {(extended?.birthday_clients_this_month?.length || 0) > 0 && (
          <ClientGroupCard
            icon={Cake}
            color="#EC4899"
            bg="#FCE7F3"
            title="Aniversariantes do Mês"
            subtitle="Mande mensagem de carinho e gere fidelidade"
            clients={extended?.birthday_clients_this_month || []}
            emptyMsg=""
            sendWhatsApp={sendWhatsApp}
            msgTemplate={(c) => `🎉 ${c.name}, parabéns pelo seu mês de aniversário! Como presente, preparamos um desconto especial para você. Vem nos visitar! ✨`}
            testId="group-birthdays"
            filterTo="/clientes?filter=birthdays"
          />
        )}

        {/* VIPs */}
        {(extended?.vip_clients?.length || 0) > 0 && (
          <ClientGroupCard
            icon={Crown}
            color="#CBA871"
            bg="#FEF3C7"
            title="Clientes VIP"
            subtitle="Sua base mais fiel"
            clients={extended?.vip_clients || []}
            emptyMsg=""
            sendWhatsApp={sendWhatsApp}
            msgTemplate={(c) => `Olá ${c.name}! Como cliente VIP, queremos garantir o melhor horário para você este mês. Quando podemos te atender? 💎`}
            testId="group-vip"
            filterTo="/clientes?filter=vip"
          />
        )}
      </div>

      {/* Atalhos de Campanha removidos: tudo unificado em /automacoes (Filtros e Leads) */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ClientGroupCard
          icon={UserX}
          color="#DC2626"
          bg="#FEE2E2"
          title={`Faltaram (semana)`}
          subtitle={`${extended?.absent_clients_week_count || 0} cliente(s) que agendaram e não compareceram nos últimos 7 dias`}
          clients={extended?.absent_clients_week || []}
          showLastVisit
          formatRelativeDate={formatRelativeDate}
          emptyMsg="Ninguém faltou essa semana 🎉"
          sendWhatsApp={sendWhatsApp}
          msgTemplate={(c) => `Oi ${(c.name||'').split(' ')[0]}! Notei que não conseguimos te receber${c.missed_date ? ` no dia ${new Date(c.missed_date).toLocaleDateString('pt-BR')}` : ''}. Tudo bem? Quer que eu te encaixe em outro horário?`}
          testId="group-absent-week"
          filterTo="/agenda?status=nao_compareceu&days=7"
        />

        <ClientGroupCard
          icon={UserX}
          color="#F59E0B"
          bg="#FEF3C7"
          title={`Faltaram (mês)`}
          subtitle={`${extended?.absent_clients_month_count || 0} cliente(s) que agendaram e não compareceram nos últimos 30 dias`}
          clients={extended?.absent_clients_month || []}
          showLastVisit
          formatRelativeDate={formatRelativeDate}
          emptyMsg="Ninguém faltou no mês 🎉"
          sendWhatsApp={sendWhatsApp}
          msgTemplate={(c) => `Olá ${(c.name||'').split(' ')[0]}! Sentimos sua falta — tivemos um agendamento${c.missed_date ? ` no dia ${new Date(c.missed_date).toLocaleDateString('pt-BR')}` : ''} que não foi confirmado. Posso te encaixar de novo?`}
          testId="group-absent-month"
          filterTo="/agenda?status=nao_compareceu&days=30"
        />
      </div>

      {/* Alerts row — clickable shortcuts to detail pages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => navigate("/comandas")}
          className="text-left w-full"
          data-testid="alert-invoices-link"
        >
          <Card className="border border-border bg-card p-5 shadow-soft rounded-md transition-all hover:shadow-md hover:border-foreground/30 cursor-pointer group" data-testid="alert-invoices">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center group-hover:bg-foreground/10 transition-colors">
                <Receipt className="h-5 w-5 text-foreground" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-heading">
                  Cobranças Pendentes
                </div>
                <div className="text-2xl font-light tracking-tight font-heading">
                  {loading ? "—" : stats?.pending_invoices}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                Abrir →
              </div>
            </div>
          </Card>
        </button>

        <button
          type="button"
          onClick={() => navigate("/mensagens")}
          className="text-left w-full"
          data-testid="alert-messages-link"
        >
          <Card className="border border-border bg-card p-5 shadow-soft rounded-md transition-all hover:shadow-md hover:border-foreground/30 cursor-pointer group" data-testid="alert-messages">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center group-hover:bg-foreground/10 transition-colors">
                <MessageCircle className="h-5 w-5 text-foreground" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-heading">
                  Mensagens Não Lidas
                </div>
                <div className="text-2xl font-light tracking-tight font-heading">
                  {loading ? "—" : stats?.unread_messages}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                Abrir →
              </div>
            </div>
          </Card>
        </button>
      </div>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Meta de Faturamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSettings} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Meta Mensal (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settingsForm.monthly_revenue_target}
                onChange={(e) => setSettingsForm({ ...settingsForm, monthly_revenue_target: e.target.value })}
                placeholder="Ex: 20000"
                required
                data-testid="settings-monthly-target-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Quanto você quer faturar este mês</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Dias úteis por mês</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={settingsForm.business_days_per_month}
                onChange={(e) => setSettingsForm({ ...settingsForm, business_days_per_month: e.target.value })}
                required
                data-testid="settings-business-days-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Usado para calcular meta diária base</p>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="settings-save-button">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientGroupCard({ icon: Icon, color, bg, title, subtitle, clients, emptyMsg, showLastVisit, formatRelativeDate, sendWhatsApp, msgTemplate, testId, filterTo }) {
  const navigate = useNavigate();
  return (
    <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
          <Icon className="h-5 w-5" strokeWidth={1.5} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        {filterTo && (
          <button
            type="button"
            onClick={() => navigate(filterTo)}
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            data-testid={`${testId}-view-all`}
            title="Ver todos com filtro aplicado"
          >
            Ver todos →
          </button>
        )}
      </div>

      {clients.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-6">{emptyMsg}</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {clients.slice(0, 15).map((c) => (
            <div
              key={c.client_id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/clientes?focus=${c.client_id}`)}
              className="flex items-center justify-between gap-2 p-2.5 rounded-md hover:bg-secondary/60 transition-colors text-sm cursor-pointer"
              data-testid={`${testId}-client-${c.client_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.name}</div>
                {showLastVisit && (
                  <div className="text-xs text-muted-foreground">
                    Última visita: {formatRelativeDate ? formatRelativeDate(c.last_visit) : c.last_visit || "—"}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs flex-shrink-0"
                onClick={(e) => { e.stopPropagation(); sendWhatsApp(c, msgTemplate(c)); }}
                title="Enviar WhatsApp"
                data-testid={`whatsapp-${c.client_id}`}
              >
                <Phone className="h-3 w-3" strokeWidth={1.5} style={{ color: "#25D366" }} />
              </Button>
            </div>
          ))}
          {clients.length > 15 && (
            <p className="text-xs text-muted-foreground text-center pt-2">+{clients.length - 15} clientes</p>
          )}
        </div>
      )}
    </Card>
  );
}

const STATUS_INFO = {
  agendado: { label: "Agendado", color: "#3B82F6", bg: "#DBEAFE", icon: ClockIcon },
  confirmado: { label: "Confirmado", color: "#CBA871", bg: "#FEF3C7", icon: CheckCircle2 },
  atendendo: { label: "Atendendo", color: "#7C3AED", bg: "#EDE9FE", icon: ClockIcon },
  concluido: { label: "Finalizado", color: "#166534", bg: "#D1FAE5", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", color: "#737373", bg: "#F3F4F6", icon: XCircle },
  nao_compareceu: { label: "Não compareceu", color: "#DC2626", bg: "#FEE2E2", icon: XCircle },
  remarcado: { label: "Remarcado", color: "#F59E0B", bg: "#FEF3C7", icon: ClockIcon },
};

function BreakdownRow({ item, formatCurrency }) {
  const status = STATUS_INFO[item.status] || STATUS_INFO.agendado;
  const StatusIcon = status.icon;
  const time = new Date(item.date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const diffPositive = item.diff > 0;
  const diffNegative = item.diff < 0;

  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-md hover:bg-secondary/50 transition-colors text-sm flex-wrap sm:flex-nowrap"
      data-testid={`breakdown-${item.appointment_id}`}
    >
      <div className="w-12 flex-shrink-0 text-xs font-medium text-muted-foreground">{time}</div>
      <div className="flex-shrink-0">
        <span
          className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1"
          style={{ background: status.bg, color: status.color }}
        >
          <StatusIcon className="h-2.5 w-2.5" strokeWidth={2} />
          {status.label}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{item.client_name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {item.services.join(", ")}
          {item.employee_name && ` · ${item.employee_name}`}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-xs text-muted-foreground">
          Prev: <span className="text-foreground font-medium">{formatCurrency(item.expected)}</span>
        </div>
        {item.realized !== null && (
          <div className="text-xs flex items-center gap-1 justify-end">
            <span className="text-muted-foreground">Real:</span>
            <span style={{ color: "#166534" }} className="font-medium">{formatCurrency(item.realized)}</span>
            {item.diff !== 0 && (
              <span
                className="text-[10px] font-medium ml-1"
                style={{ color: diffPositive ? "#166534" : "#991B1B" }}
              >
                ({diffPositive ? "+" : ""}{formatCurrency(item.diff)})
              </span>
            )}
          </div>
        )}
        {item.payment_method && (
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">
            {item.payment_method}
          </div>
        )}
      </div>
    </div>
  );
}

